import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { Add } from '@mui/icons-material';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { GridApi, GridReadyEvent, RowDragEndEvent } from 'ag-grid-community';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Container from 'src/components/atoms/Container';
import { CustomNoRowsOverlay } from 'src/components/molecules/NoRowsOverlay/NoRowsOverlay';
import { StyleTimelineSettingsActionsRenderer } from 'src/components/molecules/StyleTimelineSettingsActionsRenderer/StyleTimelineSettingsActionsRenderer';
import { defaultNonSortableColDef } from 'src/components/organisms/DataTable/columns/default-options';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { DeleteDialog } from 'src/components/organisms/DeleteDialog/DeleteDialog';
import { SmartModuleTitleCard } from 'src/components/organisms/SmartModuleTitleCard/SmartModuleTitleCard';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { StyleTimelineSettingsRow } from 'src/domain/table/style-timeline-settings.row';
import { useCustomAgGridNavigation } from 'src/hooks/ag-grid/useCustomAgGridNavigation';
import { useCompositePartner } from 'src/hooks/partner/useCompositePartner';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { useSnackbar } from 'src/hooks/snackbar/useSnackbar';
import { useStyleTimelineSettingsDelete } from 'src/hooks/style-timeline-settings/save-changes/useStyleTimelineSettingsDelete';
import { useStyleTimelineSettingsSaveChanges } from 'src/hooks/style-timeline-settings/save-changes/useStyleTimelineSettingsSaveChanges';
import { useStyleTimelineSettingsUpdate } from 'src/hooks/style-timeline-settings/save-changes/useStyleTimelineSettingsUpdateSettings';
import { useStyleTimelineSettingsColumns } from 'src/hooks/style-timeline-settings/table/useStyleTimelineSettingsColumns';
import { useStyleTimelineSettingsRows } from 'src/hooks/style-timeline-settings/table/useStyleTimelineSettingsRows';
import {
    CreatePartnerStyleTimelineSettingInput,
    UpdatePartnerStyleTimelineSettingInput,
} from 'src/infrastructure/rest-api/api-types';
import { DefaultLifeSpanEditDialog, EditableStyleTimelineSettingRow } from './DefaultLifeSpanEditDialog';

const mapToSettingInput = (
    data: EditableStyleTimelineSettingRow,
    priority: number,
    partnerCompositeId: number
): CreatePartnerStyleTimelineSettingInput => {
    return {
        lifespanWeeks: data.lifeSpanWeeks ?? 1,
        storefrontTypeId: data.storefrontTypeId ?? null,
        styleCategoryId: data.styleCategoryId ?? null,
        styleSubCategoryId: data.styleSubCategoryId ?? null,
        productLineGroupId: data.productLineGroupId ?? null,
        businessModelId: data.businessModelId ?? null,
        businessModelGroupId: data.businessModelGroupId ?? null,
        priority,
        partnerCompositeId,
    };
};

const mapToUpdateSettingInput = (data: StyleTimelineSettingsRow): UpdatePartnerStyleTimelineSettingInput => {
    return {
        id: data.id,
        priority: data.priority,
        lifespanWeeks: data.lifeSpanWeeks,
        storefrontTypeId: data.storefrontTypeId ?? null,
        styleCategoryId: data.styleCategoryId ?? null,
        styleSubCategoryId: data.styleSubCategoryId ?? null,
        productLineGroupId: data.productLineGroupId ?? null,
        businessModelId: data.businessModelId ?? null,
        businessModelGroupId: data.businessModelGroupId ?? null,
    };
};

export const byPriority = (a: StyleTimelineSettingsRow, b: StyleTimelineSettingsRow) => {
    return a.priority - b.priority;
};

export const StyleTimelineSettings: FC = () => {
    const { data: settingsData, loading: rowsLoading, error } = useStyleTimelineSettingsRows();
    const defaultColumns = useStyleTimelineSettingsColumns();
    const [rows, setRows] = useState<StyleTimelineSettingsRow[] | undefined>(undefined);

    const showSnackbar = useSnackbar();
    const showChangesSnackbar = useChangesSnackbar();

    const compositePartner = useCompositePartner();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedRow, setSelectedRow] = useState<EditableStyleTimelineSettingRow | null>(null);
    const saveChanges = useStyleTimelineSettingsSaveChanges();
    const updateSettings = useStyleTimelineSettingsUpdate();
    const deleteSetting = useStyleTimelineSettingsDelete();
    const [rowPendingDeletion, setRowPendingDeletion] = useState<StyleTimelineSettingsRow | null>(null);

    const handleUpdate = useCallback(
        (updatedRows: StyleTimelineSettingsRow[]) => {
            if (!compositePartner || !updatedRows) {
                // biome-ignore lint/suspicious/noConsole: provide better reason
                console.warn('No composite partner found!');
                return;
            }

            setRows(updatedRows);
            setIsSaving(true);
            updateSettings(updatedRows.map(mapToUpdateSettingInput))
                .then(() => {
                    setIsSaving(false);
                    setIsDialogOpen(false);
                    showChangesSnackbar(undefined, { showCubeTimer: false });
                })
                .catch((error) => {
                    setIsSaving(false);
                    showChangesSnackbar(error);
                });
        },
        [compositePartner, showChangesSnackbar, updateSettings]
    );

    const handleClose = useCallback(
        (data: EditableStyleTimelineSettingRow | null) => {
            if (!data) {
                // close dialog - cancel was clicked
                setIsDialogOpen(false);
                return;
            }

            if (!compositePartner) {
                // biome-ignore lint/suspicious/noConsole: provide better reason
                console.warn('No composite partner found!');
                return;
            }

            if (data && !data.id) {
                setIsSaving(true);
                saveChanges(mapToSettingInput(data, (rows ?? []).length, compositePartner.id))
                    .then(() => {
                        setIsSaving(false);
                        setIsDialogOpen(false);
                        showChangesSnackbar(undefined, { showCubeTimer: false });
                    })
                    .catch((error) => {
                        setIsSaving(false);
                        showChangesSnackbar(error);
                    });
            } else if (data?.id) {
                const newRows = (rows ?? []).map((row) => {
                    if (row.id === data.id) {
                        return {
                            ...row,
                            styleCategoryId: data.styleCategoryId,
                            storefrontTypeId: data.storefrontTypeId,
                            styleSubCategoryId: data.styleSubCategoryId,
                            productLineGroupId: data.productLineGroupId,
                            businessModelId: data.businessModelId,
                            businessModelGroupId: data.businessModelGroupId,
                            lifeSpanWeeks: data.lifeSpanWeeks ?? 1,
                        };
                    }
                    return row;
                });
                handleUpdate(newRows);
            }
        },
        [compositePartner, rows, saveChanges, showChangesSnackbar, handleUpdate]
    );

    const handleEditRow = useCallback((data: StyleTimelineSettingsRow) => {
        setSelectedRow(data);
        setIsDialogOpen(true);
    }, []);

    const handleDeleteRow = useCallback(() => {
        if (rowPendingDeletion) {
            deleteSetting(rowPendingDeletion.id)
                .then(() => showChangesSnackbar(undefined, { showCubeTimer: false }))
                .catch((error: unknown) => {
                    showChangesSnackbar(error);
                });
            setRowPendingDeletion(null);
        }
    }, [deleteSetting, rowPendingDeletion, showChangesSnackbar]);

    const handleCancelDeletion = useCallback(() => {
        setRowPendingDeletion(null);
    }, []);

    const showDeleteDialog = useCallback((data: StyleTimelineSettingsRow) => {
        setRowPendingDeletion(data);
    }, []);

    const handleChangePrioritization = useCallback(
        (priority: number, newPriority: number) => {
            if (newPriority < 0) {
                return;
            }

            const lowestPriority = Math.min(priority, newPriority);
            const highestPriority = Math.max(priority, newPriority);

            const rowsToUpdate =
                rows
                    ?.filter((row) => row.priority >= lowestPriority && row.priority <= highestPriority)
                    .map((row) => {
                        return {
                            ...row,
                            priority:
                                row.priority === priority
                                    ? newPriority
                                    : row.priority - 1 * Math.sign(newPriority - priority),
                        };
                    }) ?? [];

            const newRows = rows
                ?.filter((row) => row.priority < lowestPriority || row.priority > highestPriority)
                .concat(rowsToUpdate)
                .toSorted(byPriority);

            handleUpdate(newRows ?? []);
        },
        [handleUpdate, rows]
    );

    const openDialog = useCallback(() => {
        setIsDialogOpen(true);
        setSelectedRow(null);
    }, []);

    const loading = rowsLoading;

    useEffect(() => {
        setRows(loading ? undefined : settingsData?.toSorted(byPriority));
    }, [loading, settingsData]);

    const maxPriority = useMemo(() => {
        return rows?.reduce((acc, row) => Math.max(acc, row.priority), 0);
    }, [rows]);

    const handleDragEnd = useCallback(
        (event: RowDragEndEvent) => {
            const draggedData = event.node?.data;
            const overIndex = event.overIndex;

            if (!draggedData || overIndex == null || !rows) return;

            const overData = rows[overIndex];
            if (!overData) return;

            const oldPriority = draggedData.priority;
            const newPriority = overData.priority;

            handleChangePrioritization(oldPriority, newPriority);
        },
        [rows, handleChangePrioritization]
    );

    const columns = useMemo(() => {
        return [
            ...defaultColumns,
            {
                headerName: 'Actions',
                field: 'actions',
                cellRenderer: StyleTimelineSettingsActionsRenderer,
                minWidth: 140,
            },
        ];
    }, [defaultColumns]);

    const noRowsOverlayComponentParams = useMemo(() => {
        return {
            error: !!error,
            noRowsMessageFunc: () => (error ? error.message : 'No rules yet. Add a new rule to get started.'),
        };
    }, [error]);

    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const onGridReady = useCallback(({ api }: GridReadyEvent) => {
        setGridApi(api);
    }, []);

    useCustomAgGridNavigation({
        api: gridApi,
        tableWrapperRef,
    });

    useKeyboardShortcut({
        shortcutName: 'NEW',
        callback: (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!isDialogOpen) {
                openDialog();
            }
        },
    });

    useKeyboardShortcut({
        shortcutName: 'EDIT/VIEW',
        callback: (event) => {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApi?.getSelectedRows();
            if (selectedRows && selectedRows.length === 1) {
                const data = selectedRows[0] as StyleTimelineSettingsRow;
                handleEditRow(data);
            } else {
                showSnackbar('Please select a single rule to edit.', 'warning');
            }
        },
    });

    useKeyboardShortcut({
        shortcutName: 'DELETE',
        callback: (event) => {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApi?.getSelectedRows();
            if (selectedRows && selectedRows.length === 1) {
                const data = selectedRows[0] as StyleTimelineSettingsRow;
                showDeleteDialog(data);
            } else {
                showSnackbar('Please select a single rule to delete.', 'warning');
            }
        },
    });

    return (
        <Stack spacing={2}>
            <SmartModuleTitleCard />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    data-testid="addCampaign"
                    onClick={openDialog}
                    size="medium"
                    variant="contained"
                    color="info"
                    startIcon={<Add />}
                >
                    Add new rule
                </Button>
            </Box>
            <Container>
                <Stack
                    sx={{
                        mt: 2,
                        gap: 1,
                    }}
                >
                    {loading && (
                        <Stack
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <CircularProgress />
                        </Stack>
                    )}

                    {!loading && rows && rows.length > 0 && (
                        <Stack
                            sx={{
                                mb: 2,
                                gap: '0.5rem',
                            }}
                        >
                            <Typography>
                                The rules below are used to automatically apply the default life span value to new style
                                options added to the system.
                            </Typography>
                            <Typography>
                                The rules are applied from top to bottom and when a rule is matched, no further rules
                                will be processed.
                            </Typography>
                        </Stack>
                    )}

                    <TableWrapper sx={{ flex: 1 }} className="ag-theme-alpine-custom-compact" ref={tableWrapperRef}>
                        <DataTable
                            onGridReady={onGridReady}
                            autoSizeToFit
                            isNumberTable={false}
                            loading={loading}
                            columns={columns}
                            defaultColDef={defaultNonSortableColDef}
                            rows={rows}
                            rowDragManaged
                            animateRows
                            onRowDragEnd={handleDragEnd}
                            context={{
                                handleChangePrioritization,
                                maxPriority,
                                handleEditRow,
                                showDeleteDialog,
                            }}
                            noRowsOverlayComponent={CustomNoRowsOverlay}
                            noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                            rowSelection={{
                                mode: 'singleRow',
                                checkboxes: false,
                                enableClickSelection: true,
                            }}
                            suppressCellFocus
                        />
                    </TableWrapper>
                </Stack>
                {isDialogOpen && (
                    <DefaultLifeSpanEditDialog
                        isSaving={isSaving}
                        data={selectedRow}
                        open={isDialogOpen}
                        onClose={handleClose}
                    />
                )}
                {rowPendingDeletion && (
                    <DeleteDialog
                        open={!!rowPendingDeletion}
                        onCancel={handleCancelDeletion}
                        onDelete={handleDeleteRow}
                        title="Delete rule?"
                        content="Are you sure you want to delete this rule? Deleting is permanent and cannot be undone."
                    />
                )}
            </Container>
        </Stack>
    );
};
